'use client';

import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Container, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import clsx from "clsx";
import sharedStyles from "./home-shared.module.css";
import ScrollDownArrows from "@/app/features/shared/__to_be_integrated/ui/ScrollDownArrows";

export function HomeHeroFrame({
    title,
    backgroundImage,
}: {
    title: ReactNode;
    backgroundImage: ReactNode;
}) {
    return (
        <span className="relative">
            <Box
                component="section"
                className={clsx("relative w-screen bottom-fade", sharedStyles.heroBottomFade)}
                sx={{
                    height: "calc(100vh - var(--header-height))",
                }}
            >
                {backgroundImage}
                {title}
            </Box>
            <ScrollDownArrows />
        </span>
    );
}

export function HomeIntroFrame({ children, }: { children: ReactNode; }) {
    return (
        <Container sx={{ py: 2 }}>
            <Divider sx={{ my: 2 }} />
            {children}
        </Container>
    );
}

export function HomeTilesFrame({ children }: { children: ReactNode }) {
    return (
        <Box
            className="flex flex-auto min-w-0 min-h-0 w-full justify-center"
            sx={{ p: { xs: 2, md: 4 }, maxWidth: "var(--content-max-width)" }}
        >
            <Grid className="flex flex-auto" spacing={2} container columns={{ xs: 1, md: 3 }}>
                {children}
            </Grid>
        </Box>
    );
}

export function HomeTileCardFrame({
    children,
    revealOnView = false,
    animated = true,
    revealDelay = "0s",
    className,
}: {
    children: ReactNode;
    revealOnView?: boolean;
    animated?: boolean;
    revealDelay?: string;
    className?: string;
}) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(!revealOnView);

    useEffect(() => {
        if (!revealOnView || isVisible || !ref.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting) return;
                setIsVisible(true);
                observer.disconnect();
            },
            {
                threshold: 0.1,
                rootMargin: "0px 0px -5% 0px",
            }
        );

        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [isVisible, revealOnView]);

    return (
        <div
            ref={ref}
            className={clsx(
                sharedStyles.tileCard,
                !animated && sharedStyles.tileCardStatic,
                animated && isVisible && sharedStyles.tileCardVisible,
                className
            )}
            style={{ "--tile-delay": revealDelay } as CSSProperties}
        >
            {children}
        </div>
    );
}
